import React, {memo, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '~/redux/root-store';
import {useInput, useNotification} from '~/hooks';
import {
  Col,
  UiImage,
  UiText,
  UiQuestionnaireLayout,
  UiBox,
  UiSimpleGrid,
  UiButton,
  UiTextarea,
  UiFormErrorMessage,
  UiFormControl,
  UiInput,
} from '~/components/elements';
import QuestionForm from '~/assets/images/icons/question-form.png';
import {RootState} from '~/redux/root-reducer';
import {BIRTH_CONTROL_REQUEST_KEYS, EMERGENCY_CONTRACEPTION_KEYS} from '~/constants/medical-questionnaires';
import {Option, Relation} from '~/redux/questionnaires/types';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {spacing} from '~/utils/theme/spacing';
import {UiCard} from '~/components/elements/UiCard';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';

const Questionnaire4bScreen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REQUEST_KEYS.BRAND_USED]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const selectedBrand = useSelector((state: RootState) => state.questionnaires.selectedBrand);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REQUEST_KEYS.BRAND_USED);

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is 5 characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };
  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      brandUsed: currentAnswer?.brandUsed || '',
      selectedOption: currentAnswer?.selectedOption || '',
      selectedHappyOption: currentAnswer?.selectedHappyOption || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
    },
    {rules},
  );

  const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);

  const onClickSubmit = async () => {
    if (inputs.selectedHappyOption === 'NO') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: BIRTH_CONTROL_REQUEST_KEYS.BRAND_USED,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REQUEST_KEYS.STATEMENT_BEST_APPLIES}));
  };

  const renderHappyRelation = (relation: Relation) => {
    if (inputs.selectedHappyOption === 'NO') {
      const selectedHappyOptionData = relation?.options?.find(i => i.value === inputs.selectedHappyOption);
      return (
        <UiBox w="100%" mt={spacing.small}>
          {selectedHappyOptionData?.relations?.map(i => {
            if (i?.elementType?.name === 'inputArea') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="xl" fontWeight="600" mt={spacing.medium}>
                    {i?.questions?.default}
                  </UiText>
                  <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={i?.elementType?.placeholder}
                      value={inputs.medicationsTaking}
                      onChange={({target}) => setInputs({medicationsTaking: target.value})}
                      maxH="100px"
                    />
                    {validation?.errors?.medicationsTaking && (
                      <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
            return null;
          })}
        </UiBox>
      );
    }
    return null;
  };

  const renderDesc = () => {
    if (inputs.selectedOption === 'YES') {
      let data = {} as Relation;
      if (selectedOptionData?.relations?.length) {
        data = selectedOptionData?.relations[1];
      }
      if (!data.elementType || !data.options.length) return null;
      return (
        <UiBox w="100%" mt={spacing.medium}>
          <UiText fontSize="xl" fontWeight="bold">
            Were you happy with this option?
          </UiText>
          <UiSimpleGrid columns={2} spacing={5} mt={spacing.medium}>
            {data?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedHappyOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedHappyOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
          {renderHappyRelation(data)}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedHappyOption}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />

        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          Your last birth control purchase was
        </UiText>
        <UiCard variant="rounded" boxShadow="base" borderRadius="lg" py={2} mt={spacing.small}>
          <UiText fontWeight="600">{selectedBrand}</UiText>
        </UiCard>

        {renderDesc()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4bScreen;
